import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { UiCartridgeRolesEditSuborganizationsComponent } from '@markant-platform/shared/authorizer/ui';
import { ScrollIndicatorDirective } from '@markant-platform/shared/util-scroll';
import { SelectableSuborganization } from '@markant-platform/shared/organization/model';
import { BehaviorSubject } from 'rxjs';
import {
  EditRoleSuborganizationAssignments,
  EditSuborganizationsRoleAssignmentFlow,
} from '@markant-platform/shared/authorizer/model';
import { DefaultValuePipe } from '@markant-platform/shared/util-pipes';

@Component({
  selector: 'markant-platform-cartridge-role-edit-suborganizations-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    TranslocoModule,
    ModalBridgeComponent,
    UiCartridgeRolesEditSuborganizationsComponent,
    ScrollIndicatorDirective,
    DefaultValuePipe,
  ],
  templateUrl: './cartridge-role-edit-suborganizations-dialog.component.html',
  styleUrl: './cartridge-role-edit-suborganizations-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartridgeRoleEditSuborganizationsDialogComponent implements OnInit {
  @Input()
  isDialogOpen = false;

  @Input()
  editRoleSuborganizationAssignments!: EditRoleSuborganizationAssignments;

  @Input()
  groupName?: string;

  @Output()
  protected subOrganizationsSubmitted: EventEmitter<EditRoleSuborganizationAssignments> = new EventEmitter();

  @Output()
  protected cancel: EventEmitter<void> = new EventEmitter();

  selectedSuborganizations$: BehaviorSubject<SelectableSuborganization[]> = new BehaviorSubject<
    SelectableSuborganization[]
  >([]);

  protected readonly EditSuborganizationsRoleAssignmentFlow = EditSuborganizationsRoleAssignmentFlow;
  protected selectedSuborganizations: SelectableSuborganization[] = [];
  protected selectableSuborganizations$: BehaviorSubject<SelectableSuborganization[]> = new BehaviorSubject<
    SelectableSuborganization[]
  >([]);
  protected selectableSuborganizations: SelectableSuborganization[] = [];
  protected shouldEnableOverallResponsibilityForAdding = false;

  ngOnInit(): void {
    this.selectableSuborganizations = this.editRoleSuborganizationAssignments.suborganizations;
    this.selectedSuborganizations = this.editRoleSuborganizationAssignments.suborganizations.filter(s => s.selected);

    this.selectedSuborganizations.sort((a, b) => a.name.localeCompare(b.name));
    this.selectableSuborganizations$.next(this.selectableSuborganizations);
    this.selectedSuborganizations$.next(this.selectedSuborganizations);
    this.shouldEnableOverallResponsibilityForAdding = this.selectedSuborganizations.length > 0;
  }

  closeDialog(): void {
    this.isDialogOpen = false;
    this.cancel.next();
  }

  onSubmit(): void {
    this.isDialogOpen = false;
    this.subOrganizationsSubmitted.emit({
      ...this.editRoleSuborganizationAssignments,
      suborganizations: this.selectedSuborganizations,
    });
  }

  onDeleteAllSuborganizationAssignmentsClick(): void {
    this.selectableSuborganizations.forEach(s => (s.selected = false));
    this.selectedSuborganizations = [];
    this.shouldEnableOverallResponsibilityForAdding = false;

    this.selectableSuborganizations$.next(this.selectableSuborganizations);
    this.selectedSuborganizations$.next(this.selectedSuborganizations);
  }

  addSuborganization(subOrganization: SelectableSuborganization): void {
    this.selectedSuborganizations.push(subOrganization);
    this.selectedSuborganizations.sort((a, b) => a.name.localeCompare(b.name));
    this.selectedSuborganizations$.next(this.selectedSuborganizations);
    this.shouldEnableOverallResponsibilityForAdding = true;

    subOrganization.selected = true;
    this.selectableSuborganizations$.next(this.selectableSuborganizations);
  }

  removeSuborganization(subOrganization: SelectableSuborganization): void {
    const index = this.selectedSuborganizations.indexOf(subOrganization, 0);
    if (index > -1) {
      this.selectedSuborganizations.splice(index, 1);
      this.selectedSuborganizations$.next(this.selectedSuborganizations);
    }

    subOrganization.selected = false;
    if (this.selectedSuborganizations.length === 0) {
      this.shouldEnableOverallResponsibilityForAdding = false;
    }
    this.selectableSuborganizations$.next(this.selectableSuborganizations);
  }
}
